import React, { useState, useMemo } from "react";
import { format } from "date-fns";
import { BUSINESS_COLUMNS } from "./columns";
import { BusinessData } from "../../../../utils/data/data";
import { FaMinusCircle, FaPlusCircle } from "react-icons/fa";
import {
  useTable,
  useRowSelect,
  useFilters,
  useGlobalFilter,
  usePagination,
  useExpanded
} from "react-table";
import "./checklisttable.scss";
import PropTypes from "prop-types";
import { WHITESPACE_REGEX } from "../../../../utils/constants/appConstants";
import { ddMMMyyyy } from "../../../../utils/constants/appConstants";
import { Tooltip as ReactTooltip } from "react-tooltip";
import { useSalesChecklistComment } from "../../../../hooks/useSalesChecklistComment";

const businessSectionEnumMap = {
  "FT credit score": "FT_CREDIT_SCORE",
  "Credit Score": "CREDIT_SCORE_BUSINESS",
  GSAs: "GSAS",
  PPSRs: "PPSRS",
  "Time in Business": "TIME_IN_BUSINESS",
  "Ownership:": "OWNERSHIP_BUSINESS"
};

const businessHoverTextMap = {
  "FT credit score": "Green > 0.8, Orange > 0.5, RED ≤ 0.5",
  "Credit Score": "Green > 600, Orange > 500, Red ≤ 500"
};

const businessCategoryMap = Object.fromEntries(
  Object.entries(businessSectionEnumMap).map(([label, section]) => [
    section,
    label
  ])
);

function mapBusinessData(baseRows, salesBusinessInfo) {
  if (!Array.isArray(salesBusinessInfo) || salesBusinessInfo.length === 0)
    return baseRows;
  const rows = baseRows.map((row) => ({ ...row }));
  salesBusinessInfo.forEach((item) => {
    const businessLabel = businessCategoryMap[item.category];
    if (!businessLabel) return;
    const row = rows.find((r) => r.business === businessLabel);
    if (row) {
      row.numbers = item.value ? item.value : "";
      row.details = item.details ? item.details : "";
      row.valueColor = item.valueColor ? item.valueColor : "";
      row.detailsColor = item.detailsColor ? item.detailsColor : "";
    }
  });
  return rows;
}

const BusinessTable = ({ salesBusinessInfo, userId, salesAdditionalNote }) => {
  const columns = useMemo(() => {
    const baseCols = [...BUSINESS_COLUMNS];

    baseCols[0].Cell = function BusinessTableCell({ cell }) {
      const { hoverText, sectionEnum } = cell.row.original;
      const tooltipId = `tooltip-${sectionEnum || cell.value.replace(WHITESPACE_REGEX, "-")}`;
      return (
        <>
          <div
            data-tooltip-id={tooltipId}
            data-tooltip-content={hoverText}
            className={hoverText ? "tooltip-enabled" : undefined}
          >
            {cell.value}
          </div>
          {hoverText && (
            <ReactTooltip
              id={tooltipId}
              className="tooltipcustom tooltip-arrow-left"
              place="bottom-start"
              delayShow={300}
            />
          )}
        </>
      );
    };

    baseCols[1].Cell = function NumbersCell({ cell }) {
      const isColored = cell?.row?.original?.valueColor;
      return (
        <div
          className={`owner-property-cell${isColored ? " colored-cell" : ""}`}
          style={isColored ? { backgroundColor: isColored } : undefined}
        >
          {cell.value}
        </div>
      );
    };

    baseCols[2].Cell = function DetailsCell({ cell }) {
      const isColored = cell?.row?.original?.detailsColor;
      return (
        <div
          className={`owner-property-cell${isColored ? " colored-cell" : ""}`}
          style={isColored ? { backgroundColor: isColored } : undefined}
        >
          {cell.value}
        </div>
      );
    };

    return baseCols;
  }, []);

  const data = useMemo(() => {
    return mapBusinessData(BusinessData, salesBusinessInfo).map((row) => ({
      ...row,
      sectionEnum: businessSectionEnumMap[row.business] || null,
      hoverText: businessHoverTextMap[row.business] || "",
      valueColor: row.valueColor ?? null,
      detailsColor: row.detailsColor ?? null
    }));
  }, [salesBusinessInfo]);

  const notesLookup = useMemo(() => {
    if (!Array.isArray(salesAdditionalNote)) return {};
    return salesAdditionalNote.reduce((acc, item) => {
      if (item.section && typeof item.note === "string") {
        acc[item.section] = item.note;
      }
      return acc;
    }, {});
  }, [salesAdditionalNote]);

  const { getTableProps, getTableBodyProps, headerGroups, prepareRow, page } =
    useTable(
      {
        columns,
        data,
        initialState: { pageIndex: 0 }
      },
      useFilters,
      useGlobalFilter,
      useExpanded,
      usePagination,
      useRowSelect
    );

  const [expandedRows, setExpandedRows] = useState([]);

  const toggleRow = (rowId) => {
    setExpandedRows((prev) =>
      prev.includes(rowId)
        ? prev.filter((id) => id !== rowId)
        : [...prev, rowId]
    );
  };

  const isRowExpanded = (rowId) => expandedRows.includes(rowId);

  const { commentData, setCommentData, originalNotesRef, updateSalesData } =
    useSalesChecklistComment({ userId });

  return (
    <table
      className="table-fixed mt-2 mb-3 businesstable business-tab borderless_table bgless_table"
      {...getTableProps()}
    >
      <thead>
        {headerGroups.map((headerGroup) => {
          const { key, ...rest } = headerGroup.getHeaderGroupProps();
          return (
            <tr className="parentrow" {...rest} key={key}>
              <th />
              {headerGroup.headers.map((column) => (
                <th {...column.getHeaderProps()} key={column.id}>
                  <span>{column.render("Header")}</span>
                </th>
              ))}
            </tr>
          );
        })}
      </thead>

      <tbody {...getTableBodyProps()}>
        {page.map((row) => {
          prepareRow(row);
          const isExpanded = isRowExpanded(row.id);

          return (
            <React.Fragment key={row.id}>
              <tr className="parentrow" {...row.getRowProps()}>
                <td className="expand-td">
                  <span
                    className="expandbtn cursor-pointer"
                    onClick={() => toggleRow(row.id)}
                  >
                    {isExpanded ? <FaMinusCircle /> : <FaPlusCircle />}
                  </span>
                </td>

                {row.cells.map((cell) => (
                  <td key={cell.getCellProps().key} {...cell.getCellProps()}>
                    {cell.render("Cell", {
                      updateSalesData,
                      commentData,
                      setCommentData,
                      originalNotesRef,
                      rowId: row.id,
                      sectionEnum: row.original.sectionEnum,
                      noteValue: notesLookup[row.original.sectionEnum] ?? ""
                    })}
                  </td>
                ))}
              </tr>

              {isExpanded && (
                <tr className="childrow colexpand">
                  <td colSpan={columns.length + 2}>
                    <div className="tdbody">
                      {columns.map((column) => (
                        <span key={column.accessor} data-label={column.Header}>
                          {column.accessor === "date"
                            ? format(
                                new Date(row.original[column.accessor]),
                                ddMMMyyyy
                              )
                            : String(row.original[column.accessor])}
                        </span>
                      ))}
                    </div>
                  </td>
                </tr>
              )}
            </React.Fragment>
          );
        })}
      </tbody>
    </table>
  );
};

BusinessTable.propTypes = {
  salesBusinessInfo: PropTypes.array.isRequired,
  userId: PropTypes.string.isRequired,
  salesAdditionalNote: PropTypes.array,
  cell: PropTypes.shape({
    value: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    row: PropTypes.shape({
      original: PropTypes.shape({
        hoverText: PropTypes.string,
        sectionEnum: PropTypes.string,
        valueColor: PropTypes.string,
        detailsColor: PropTypes.string
      })
    })
  })
};

export default BusinessTable;
